import { CheckCircle2, Circle, FileStack, Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api, docs,
  type DocumentRow, type EstateSearch, type EstateSearchHit, type Job,
  type PackageSummary,
} from "../lib/api";
import { Explainer } from "../components/Explainer";
import { RoleButton } from "../components/RequireRole";
import { TableScroll } from "../components/TableScroll";
import { listNote, type ListStatus } from "../components/States";
import { TagRunCard } from "../components/TagRunCard";
import { DOC_TYPE_LABELS } from "../lib/api";
import { displayName, fileName } from "../lib/words";
import { roleBlock, useMe, type Me } from "../lib/me";
import {
  documentsPath, fetchIndexes, indexPackage, scanStatusFor, serverRunning,
  type IndexKind, type IndexRun, type LastIndexRun, type PackageIndexStatus, type ScanStatus,
} from "./documentsFlow";
import { utcTime } from "../components/admin/format";
import { runSearch, startLookup } from "./searchFlow";
import { documentViewPath } from "./documentViewFlow";
import "../theme/review.css";

const INDEXES: { kind: IndexKind; title: string; what: string; button: string; none: string; running: string }[] = [
  {
    kind: "notices", title: "Manufacturer notices", button: "Index notices",
    what: "Reads the text of the bulletin folders so an obsolescence check can be answered from the package. GTG's 181 files take about 2 seconds.",
    none: "No manufacturer notices found in this package.",
    running: "Reading the bulletins.",
  },
  {
    kind: "visits", title: "Site visit reports", button: "Index visit reports",
    what: "Reads every site visit report with free local OCR so a component's history can be asked for. GTG's 74 reports take about 5 minutes.",
    none: "No site visit reports found in this package.",
    running: "Reading the visit reports. This takes a few minutes and carries on if you leave this page.",
  },
];

/** A file's own name without its extension, shown where no index row gives it a title. */
export function fileStem(name: string): string {
  return name.replace(/\.[A-Za-z0-9]{1,5}$/, "");
}

/** A label for one finished run: when, by whom, how many documents. */
function lastIndexed(prev: LastIndexRun): string {
  const n = prev.documents ?? 0;
  return `Last indexed ${utcTime(prev.at)} by ${prev.by}: ${n.toLocaleString("en-US")} document${n === 1 ? "" : "s"}.`;
}

/** What the server says of an index nobody started on this page: running, failed, done, or never. */
function ServerRun({ prev, running }: { prev: LastIndexRun | null; running: string }) {
  if (!prev) return <span className="muted">Not indexed yet.</span>;
  if (prev.status === "running") {
    return (
      <>
        <span className="pill info"><span className="row" style={{ gap: 6 }}><Loader2 size={13} className="spin" /> Reading</span></span>
        <span className="muted"> Started {utcTime(prev.at)} by {prev.by}. {running}</span>
      </>
    );
  }
  if (prev.status === "failed") {
    return (
      <>
        <span className="pill fail">failed</span> <span className="muted">{utcTime(prev.at)}: {prev.error ?? "the index did not finish"}</span>
        {prev.last_done && <span className="muted"> {lastIndexed(prev.last_done)}</span>}
      </>
    );
  }
  return <span className="muted">{lastIndexed(prev)}</span>;
}

/**
 * The two package indexes an engineer starts by hand. A run started here shows its progress; with
 * none started, the last run recorded for the package (`last`) is shown, so a reload keeps it.
 */
export function PackageIndexes({ pkg, me, runs, onIndex, last = null }: {
  pkg: string; me: Me | null | undefined; runs: Record<IndexKind, IndexRun>; onIndex: (kind: IndexKind) => void;
  last?: Pick<PackageIndexStatus, IndexKind> | null;
}) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="section-title"><h3>Notices and visit reports</h3></div>
      {INDEXES.map((ix) => {
        const run = runs[ix.kind];
        return (
          <div key={ix.kind} className="index-row" data-index={ix.kind}>
            <div className="what"><strong>{ix.title}</strong><span>{ix.what}</span></div>
            <span style={{ fontSize: 13 }} aria-live="polite">
              {run.status === "running" && (
                <span className="pill info"><span className="row" style={{ gap: 6 }}><Loader2 size={13} className="spin" /> Reading</span></span>
              )}
              {run.status === "running" && <span className="muted"> {ix.running}</span>}
              {run.status === "done" && (run.documents > 0
                ? <><span className="pill ok">done</span> <span className="muted">{run.documents.toLocaleString("en-US")} documents indexed in {pkg}.</span></>
                : <span className="muted">{ix.none}</span>)}
              {run.status === "failed" && <><span className="pill fail">failed</span> <span className="muted">{run.error}</span></>}
              {run.status === "idle" && last && <ServerRun prev={last[ix.kind]} running={ix.running} />}
            </span>
            <RoleButton role="engineer" me={me}
                        disabled={run.status === "running" || (run.status === "idle" && last?.[ix.kind]?.status === "running")}
                        onClick={() => onIndex(ix.kind)}>
              {ix.button}
            </RoleButton>
          </div>
        );
      })}
    </div>
  );
}

/** Where the content sweep read a tag in this package, matched as estate search matches it. */
export function SweepTagHits({ tag, documents }: { tag: string; documents: Record<string, number[]> }) {
  const paths = Object.keys(documents);
  if (paths.length === 0) {
    return <p className="muted" style={{ fontSize: 13, marginTop: 10 }}>The sweep did not read {tag} on any page of this package.</p>;
  }
  return (
    <div style={{ marginTop: 10, fontSize: 13 }} data-sweep-tag={tag}>
      <strong className="mono">{tag}</strong> is read on {paths.length} document{paths.length === 1 ? "" : "s"} in this sweep:
      <ul className="evidence" style={{ marginTop: 6 }}>
        {paths.map((p) => (
          <li key={p}>
            <span className="mono">{fileName(p)}</span>
            <span className="muted">{documents[p].length === 1 ? "page" : "pages"} {documents[p].join(", ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The packages this person may read, each with its document count. The API filters by scope. */
export function PackageBrowser({ packages, status = "ready", active, onOpen, hrefFor }: {
  packages: PackageSummary[]; active: string | null; onOpen: (name: string) => void;
  /** Whether the list has answered. Empty is said only once it has, with nothing. */
  status?: ListStatus;
  /** Each tile's link. With it a tile is a link, so it has a URL and opens from the keyboard. */
  hrefFor?: (name: string) => string;
}) {
  return (
    <div className="tiles" style={{ marginBottom: 28 }}>
      {packages.map((p) => {
        const body = (
          <>
            <span className="l" style={{ display: "block" }}>{p.name}</span>
            <span className="n" style={{ display: "block" }}>{p.documents.toLocaleString("en-US")}</span>
            <span className="muted" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
              {p.index_rows.toLocaleString("en-US")} index rows
              {p.index_requires_ocr && ", index is a scan"}
            </span>
          </>
        );
        const cls = `card tile tile-button ${active === p.name ? "highlight" : ""}`;
        return hrefFor ? (
          <Link key={p.name} to={hrefFor(p.name)} className={cls} aria-current={active === p.name ? "page" : undefined}
                style={{ color: "inherit", textDecoration: "none" }}>
            {body}
          </Link>
        ) : (
          <button type="button" key={p.name} className={cls} aria-pressed={active === p.name} onClick={() => onOpen(p.name)}>
            {body}
          </button>
        );
      })}
      {(() => {
        const note = listNote(status, packages.length, "No packages mounted.", "Loading packages");
        if (!note) return null;
        return (
          <div className="card" role={status instanceof Error ? "alert" : "status"}>
            <span className="muted">{note}</span>
          </div>
        );
      })()}
    </div>
  );
}

/**
 * Estate search results, one group per package. Each row starts a UC1 lookup for the searched
 * tag in that row's package, so an engineer goes from "where is it" to extraction in one click.
 */
export function EstateResults({ result, starting, onStart, me }: {
  result: EstateSearch; starting: string | null; onStart: (hit: EstateSearchHit) => void; me: Me | null | undefined;
}) {
  if (result.results.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 20 }}>
        <strong>{result.truncated ? "No complete answer: too many similar values to read through." : "No matches in your packages."}</strong>
        <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>{result.message}</p>
      </div>
    );
  }
  const groups = new Map<string, EstateSearchHit[]>();
  for (const hit of result.results) groups.set(hit.package, [...(groups.get(hit.package) ?? []), hit]);
  return (
    <div style={{ marginBottom: 20 }}>
      <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
        {result.message}
        {result.truncated && " More pages matched than one search returns; narrow the term."}
      </p>
      {[...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([pkg, hits]) => (
        <section key={pkg} data-package={pkg} className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
          <div className="section-title" style={{ padding: "12px 16px 0" }}>
            <h3>{pkg}</h3>
            <span className="muted" style={{ fontSize: 12 }}>
              {hits.length} document{hits.length === 1 ? "" : "s"}
            </span>
          </div>
          <TableScroll label={`Matches in ${pkg}`}>
          <table>
            <thead><tr><th>Document</th><th>Pages</th><th><span className="visually-hidden">Lookup</span></th></tr></thead>
            <tbody>
              {hits.map((hit) => (
                <tr key={hit.path}>
                  <td className="mono" style={{ fontSize: 12, overflowWrap: "anywhere" }}>
                    {hit.file_name ? displayName(hit.file_name) : fileName(hit.path)}
                    <span className="muted" style={{ display: "block", fontFamily: "var(--font-sans)" }}>{pkg} package</span>
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {hit.links.map((l, i) => (
                      <span key={l.page}>
                        {i > 0 && ", "}
                        <a href={l.url} target="_blank" rel="noreferrer">page {l.page}</a>
                      </span>
                    ))}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RoleButton role="engineer" me={me} className="ghost" disabled={starting !== null} onClick={() => onStart(hit)}>
                      {starting === hit.path ? "Starting..." : "Start lookup"}
                    </RoleButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </section>
      ))}
    </div>
  );
}

/**
 * Approve a document, or withdraw an approval. Withdrawing stops UC1 reading the document, so it
 * asks first; approving does not.
 */
export function toggleApproval({ approved, file, onChange, confirm = (m: string) => window.confirm(m) }: {
  approved: boolean; file: string; onChange: (approved: boolean) => void; confirm?: (message: string) => boolean;
}): void {
  if (approved && !confirm(`Withdraw the approval for ${file}? Lookups will not read it until it is approved again.`)) return;
  onChange(!approved);
}

/** The approval state as a button that says what clicking it does. */
export function ApprovalToggle({ approved, file, onChange }: {
  approved: boolean; file: string; onChange: (approved: boolean) => void;
}) {
  const action = approved ? "Approved, click to withdraw" : "Not approved, click to approve";
  return (
    <button type="button" className="ghost approval-toggle" aria-label={`${file}: ${action}`} title={action}
            onClick={() => toggleApproval({ approved, file, onChange })}>
      <span className="row" style={{ gap: 6 }}>
        {approved ? <CheckCircle2 size={15} aria-hidden="true" /> : <Circle size={15} aria-hidden="true" />}
        {approved ? "Approved" : "Approve"}
      </span>
      {approved && <span className="hint">Click to withdraw</span>}
    </button>
  );
}

/**
 * The intake area. Approval here is not decorative: UC1 refuses to open a document that has
 * not been approved (FR-1.18), so this is where an engineer decides what the system may read.
 */
export function Documents({ query, onJobStarted }: {
  query: string; onJobStarted?: (job: Job) => void;
}) {
  const [packages, setPackages] = useState<PackageSummary[]>([]);
  const [packagesStatus, setPackagesStatus] = useState<ListStatus>("loading");
  // The open package is the URL's, so a deep link, a reload and the back button keep it.
  const { package: active = null } = useParams();
  const navigate = useNavigate();
  const [last, setLast] = useState<PackageIndexStatus | null>(null);
  const [rows, setRows] = useState<DocumentRow[]>([]);
  // The package the rows were read for, and the error if reading them failed. Until they match the
  // open package the table says it is loading, so "Nothing matches" never shows before the answer.
  const [rowsFor, setRowsFor] = useState<string | null>(null);
  const [rowsError, setRowsError] = useState<Error | null>(null);
  const loading = !!active && rowsFor !== active;
  const [local, setQ] = useState("");
  const q = query || local;
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scan, setScan] = useState<ScanStatus | null>(null);
  const { me } = useMe();
  const readOnly = roleBlock(me, "engineer") !== null;
  const [runs, setRuns] = useState<Record<string, Record<IndexKind, IndexRun>>>({});
  const idle: Record<IndexKind, IndexRun> = { notices: { status: "idle" }, visits: { status: "idle" } };
  const [term, setTerm] = useState("");
  const [searched, setSearched] = useState<{ tag: string; result: EstateSearch } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);

  async function search() {
    const tag = term.trim();
    if (!tag) return;
    setSearching(true); setSearchError(null);
    const out = await runSearch(tag);
    setSearching(false);
    setSearched(out.result ? { tag, result: out.result } : null);
    setSearchError(out.error);
  }

  async function lookup(hit: EstateSearchHit) {
    if (!searched) return;
    setStarting(hit.path); setSearchError(null);
    try {
      const job = await startLookup(searched.tag, hit);
      onJobStarted?.(job);
    } catch (e) {
      setSearchError((e as Error).message);
    } finally {
      setStarting(null);
    }
  }

  useEffect(() => {
    docs.packages()
      .then((p) => { setPackages(p); setPackagesStatus("ready"); })
      .catch((e: Error) => setPackagesStatus(e));
  }, []);

  // The tag in context is the one last searched for. The sweep status names where it was read.
  const tagInContext = searched?.tag ?? null;

  function openPackage(name: string) {
    navigate(documentsPath(name));
  }

  useEffect(() => {
    if (!active) { setRows([]); setRowsFor(null); setRowsError(null); setScan(null); setLast(null); return; }
    let live = true;
    setRowsFor(null); setRowsError(null); setSelected(new Set()); setScan(null); setLast(null);
    scanStatusFor(active, tagInContext).then((s) => { if (live) setScan(s); }).catch(() => { if (live) setScan(null); });
    fetchIndexes(active).then((s) => { if (live) setLast(s); }).catch(() => undefined);
    docs.documents(active)
      .then((r) => { if (live) { setRows(r); setRowsFor(active); } })
      .catch((e: Error) => { if (live) { setRows([]); setRowsError(e); setRowsFor(active); } });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- a package change reloads it; the tag has its own effect
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let live = true;
    scanStatusFor(active, tagInContext).then((s) => { if (live) setScan(s); }).catch(() => undefined);
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when the tag changes; opening a package fetches on its own
  }, [tagInContext]);

  async function startScan() {
    if (!active) return;
    setScan(await api.buildScan(active));
  }

  async function runIndex(kind: IndexKind) {
    if (!active) return;
    const pkg = active;
    const set = (run: IndexRun) =>
      setRuns((all) => ({ ...all, [pkg]: { ...idle, ...all[pkg], [kind]: run } }));
    set({ status: "running" });
    const out = await indexPackage(kind, pkg);
    if (kind === "visits" && out.status === "running") {
      // Queued on the server: from here the server's status is shown, exactly as after a reload.
      const fresh = await fetchIndexes(pkg).catch(() => null);
      if (fresh && fresh.package === active) setLast(fresh);
      set({ status: "idle" });
      return;
    }
    set(out);
  }

  // While the server says an index is running, read its status again, so the card moves to done
  // or failed without a reload.
  useEffect(() => {
    if (!active || !serverRunning(last)) return;
    const t = setInterval(async () => {
      const fresh = await fetchIndexes(active).catch(() => null);
      if (fresh) setLast(fresh);
    }, 5000);
    return () => clearInterval(t);
  }, [active, last]);

  // While a sweep is running, poll it. It is minutes of local OCR, so the page has to show
  // progress rather than leave the button looking broken.
  useEffect(() => {
    if (!active || scan?.status !== "building") return;
    const t = setInterval(async () => {
      const fresh = await scanStatusFor(active, tagInContext).catch(() => null);
      if (fresh) setScan(fresh);
      if (fresh && fresh.status !== "building") clearInterval(t);
    }, 4000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the tag is read when each poll fires
  }, [active, scan?.status]);

  async function setApproval(paths: string[], approved: boolean) {
    await docs.approve(paths, approved);
    setRows((rs) => rs.map((r) => (paths.includes(r.path) ? { ...r, approved } : r)));
    setSelected(new Set());
  }

  const types = ["all", ...Array.from(new Set(rows.map((r) => r.doc_type ?? "unclassified")))];
  const shown = rows.filter(
    (r) =>
      (typeFilter === "all" || (r.doc_type ?? "unclassified") === typeFilter) &&
      (!q || displayName(r.file_name).toLowerCase().includes(q.toLowerCase()) ||
        displayName(r.title).toLowerCase().includes(q.toLowerCase())),
  );

  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>Documents</h2>
      <p className="muted" style={{ marginBottom: 24 }}>
        The packages available to the proof of concept, and what has been approved for extraction.
      </p>

      <form className="row" style={{ marginBottom: 14, gap: 10, flexWrap: "wrap" }}
            onSubmit={(e) => { e.preventDefault(); void search(); }}>
        <input value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Search every package"
               placeholder="Find a tag or part number in every package, e.g. PT-403"
               style={{ flex: "1 1 260px", minWidth: 0 }} />
        <button type="submit" disabled={searching || !term.trim()}>
          <span className="row" style={{ gap: 6 }}>
            {searching ? <Loader2 size={15} /> : <Search size={15} />} Search
          </span>
        </button>
      </form>
      {searchError && <p className="pill fail" style={{ marginBottom: 14 }}>{searchError}</p>}
      {searched && <EstateResults result={searched.result} starting={starting} onStart={lookup} me={me} />}

      <PackageBrowser packages={packages} status={packagesStatus} active={active} onOpen={openPackage} hrefFor={documentsPath} />

      {!active ? (
        <div className="card placeholder">
          <Explainer>
            Select a package to see its documents. Types come from the master index where the
            package has one, and from the file otherwise, because for MSO the two disagree
            completely: typing by file name finds no data sheets, typing by index title finds them.
          </Explainer>
        </div>
      ) : (
        <>
          <div className="row" style={{ marginBottom: 14, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 18 }}>{active}</strong>
            <input value={local} onChange={(e) => setQ(e.target.value)} aria-label={`Search documents in ${active}`}
                   placeholder="Search documents..." style={{ minWidth: 0, flex: "1 1 220px", maxWidth: 320 }} />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Document type">
              {types.map((t) => <option key={t} value={t}>{t === "all" ? "all types" : DOC_TYPE_LABELS[t] ?? t.replace(/_/g, " ")}</option>)}
            </select>
            <span className="muted" style={{ fontSize: 13 }}>{shown.length} shown</span>
            {selected.size > 0 && (
              <span style={{ marginLeft: "auto" }}>
                <RoleButton role="engineer" me={me} onClick={() => setApproval([...selected], true)}>
                  Approve {selected.size} selected
                </RoleButton>
              </span>
            )}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="section-title"><h3>Content index</h3></div>
            <Explainer>
              The master index names only some of the documents that mention a tag. On MSO it
              attributes PT-403 to one document where reading the content finds four, so a lookup
              that trusts the index alone sees about a quarter of the evidence. This pass reads
              every document once with free local OCR and records which pages mention which tag.
              No paid parsing is involved, and every later lookup on this package reuses it.
            </Explainer>
            <div className="row" style={{ marginTop: 14, gap: 14, flexWrap: "wrap" }}>
              {scan?.status === "ready" && (
                <>
                  <span className="pill ok">ready</span>
                  <span className="muted" style={{ fontSize: 13 }}>
                    {scan.documents_scanned.toLocaleString()} documents read,{" "}
                    {scan.tags_found.toLocaleString()} of {scan.tags_known.toLocaleString()} index tags
                    found in content
                    {scan.content_tags_found ? `, and ${scan.content_tags_found.toLocaleString()} more tags the index does not name` : ""}
                  </span>
                  <span style={{ marginLeft: "auto" }}>
                    <RoleButton role="engineer" me={me} className="ghost" onClick={startScan}>Rebuild</RoleButton>
                  </span>
                </>
              )}
              {scan?.status === "building" && (
                <>
                  <span className="pill info">
                    <span className="row" style={{ gap: 6 }}><Loader2 size={13} /> reading</span>
                  </span>
                  <span className="muted" style={{ fontSize: 13 }}>
                    {scan.documents_total
                      ? `${scan.documents_total.toLocaleString()} documents to read. MSO takes about 16 minutes; GTG is a few hours.`
                      : "Starting."}
                  </span>
                </>
              )}
              {scan?.status === "failed" && (
                <>
                  <span className="pill fail">failed</span>
                  <span className="muted" style={{ fontSize: 13 }}>{scan.error}</span>
                  <span style={{ marginLeft: "auto" }}>
                    <RoleButton role="engineer" me={me} className="ghost" onClick={startScan}>Try again</RoleButton>
                  </span>
                </>
              )}
              {!scan && (
                <>
                  <span className="muted" style={{ fontSize: 13 }}>
                    Not built. Lookups fall back to the master index alone.
                  </span>
                  <span style={{ marginLeft: "auto" }}>
                    <RoleButton role="engineer" me={me} onClick={startScan}>Build content index</RoleButton>
                  </span>
                </>
              )}
            </div>
            {scan?.status === "ready" && tagInContext && scan.tag_documents && (
              <SweepTagHits tag={tagInContext} documents={scan.tag_documents} />
            )}
          </div>

          <TagRunCard pkg={active} me={me ?? null} />
          <PackageIndexes pkg={active} me={me} runs={runs[active] ?? idle} onIndex={(k) => void runIndex(k)} last={last} />

          <section className="card" style={{ padding: 0, overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: 32, textAlign: "center" }}>
                <Loader2 className="muted" /> <span className="muted">Reading the index...</span>
              </div>
            ) : (
              <TableScroll label={`Documents in ${active}`}>
              <table>
                <thead><tr>
                  <th style={{ width: 40 }}><span className="visually-hidden">Select</span></th>
                  <th>Document</th><th>Title</th><th>Type</th><th>Found via</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr><td colSpan={6} className="muted" role={rowsError ? "alert" : undefined}>
                      {rowsError ? `The documents could not be read: ${rowsError.message}` : rows.length === 0 ? "No documents in this package." : "Nothing matches."}
                    </td></tr>
                  )}
                  {shown.slice(0, 120).map((r) => (
                    <tr key={r.path} className="doc-row" data-doc={r.id}
                        onClick={(e) => {
                          // The row opens the document; its own controls keep their click.
                          if ((e.target as HTMLElement).closest("a, button, input, label")) return;
                          navigate(documentViewPath(active, r.id));
                        }}>
                      <td>
                        <input type="checkbox" checked={selected.has(r.path)} aria-label={`Select ${displayName(r.file_name)}`}
                               disabled={readOnly}
                               onChange={(e) => {
                                 const s = new Set(selected);
                                 if (e.target.checked) s.add(r.path); else s.delete(r.path);
                                 setSelected(s);
                               }} />
                      </td>
                      <td className="mono" style={{ fontSize: 12, overflowWrap: "anywhere" }}>
                        <Link to={documentViewPath(active, r.id)} aria-label={`Open ${displayName(r.file_name)}${r.folder ? ` in ${displayName(r.folder)}` : ""}`}>{displayName(r.file_name)}</Link>
                        {r.folder && <span className="muted" style={{ display: "block", fontFamily: "var(--font-sans)" }}>{displayName(r.folder)}</span>}
                      </td>
                      <td style={{ fontSize: 13 }}>{r.title != null ? displayName(r.title) : <span className="muted">{fileStem(displayName(r.file_name))}</span>}</td>
                      <td><span className="pill info">{DOC_TYPE_LABELS[r.doc_type ?? "unclassified"] ?? (r.doc_type ?? "").replace(/_/g, " ")}</span></td>
                      <td className="muted" style={{ fontSize: 12 }}>{r.found_via}</td>
                      <td>
                        {readOnly ? (
                          <span className="row muted" style={{ gap: 6, fontSize: 13 }}>
                            {r.approved ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                            {r.approved ? "Approved" : "Not approved"}
                          </span>
                        ) : (
                          <ApprovalToggle approved={r.approved} file={displayName(r.file_name)}
                                          onChange={(approved) => void setApproval([r.path], approved)} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </TableScroll>
            )}
          </section>
          {shown.length > 120 && (
            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              Showing the first 120 of {shown.length}. <FileStack size={12} /> Full paging is delivery-phase.
            </p>
          )}
        </>
      )}
    </div>
  );
}
